import { jsx as _jsx } from "react/jsx-runtime";
import classNames from 'classnames';
import { useCallback, useRef, cloneElement, forwardRef } from 'react';
import Transition, { ENTERED, ENTERING } from 'react-transition-group/Transition';
import transitionEndListener from '../transitionEndListener';
import triggerBrowserReflow from '../triggerBrowserReflow';
const fadeStyles = {
    [ENTERING]: 'show',
    [ENTERED]: 'show'
};
/**
 * Use the Fade component to animate an element fading in or out.
 */ const FadeFn = ({ children, timeout = 300, nodeRef: nodeRefProps, onEnter, onEntering, onEntered, onExit, onExiting, onExited, appear = false, mountOnEnter = false, unmountOnExit = false, ...props }, transitionRef)=>{
    const hasExternalNodeRef = nodeRefProps !== undefined;
    const internalNodeRef = useRef(null);
    // Om en nodeRef skickas in använder vi den, annars skapar vi en egen
    const actualRef = hasExternalNodeRef ? nodeRefProps : internalNodeRef;
    const normalizeEventHandler = useCallback((e)=>{
        if (hasExternalNodeRef) {
            return (...params)=>e(...params);
        }
        return (...params)=>e(actualRef.current, ...params);
    }, [
        hasExternalNodeRef
    ]);
    const handleEnter = useCallback((isAppearing)=>{
        triggerBrowserReflow(actualRef.current);
        if (onEnter) normalizeEventHandler(onEnter)(isAppearing);
    }, [
        onEnter,
        normalizeEventHandler
    ]);
    const handleEntering = useCallback((isAppearing)=>{
        if (onEntering) normalizeEventHandler(onEntering)(isAppearing);
    }, [
        onEntering,
        normalizeEventHandler
    ]);
    const handleEntered = useCallback((isAppearing)=>{
        if (onEntered) normalizeEventHandler(onEntered)(isAppearing);
    }, [
        onEntered,
        normalizeEventHandler
    ]);
    const handleExit = useCallback(()=>{
        if (onExit) normalizeEventHandler(onExit)();
    }, [
        onExit,
        normalizeEventHandler
    ]);
    const handleExiting = useCallback(()=>{
        if (onExiting) normalizeEventHandler(onExiting)();
    }, [
        onExiting,
        normalizeEventHandler
    ]);
    const handleExited = useCallback(()=>{
        if (onExited) normalizeEventHandler(onExited)();
    }, [
        onExited,
        normalizeEventHandler
    ]);
    const endListener = useCallback((handler)=>{
        transitionEndListener(actualRef.current, handler);
    }, []);
    return /*#__PURE__*/ _jsx(Transition, {
        nodeRef: actualRef,
        ref: transitionRef,
        addEndListener: endListener,
        timout: timeout,
        onEnter: handleEnter,
        onEntering: handleEntering,
        onEntered: handleEntered,
        onExit: handleExit,
        onExiting: handleExiting,
        onExited: handleExited,
        appear: appear,
        mountOnEnter: mountOnEnter,
        unmountOnExit: unmountOnExit,
        ...props,
        children: (status)=>/*#__PURE__*/ cloneElement(children, {
                className: classNames('fade', children.props.className, fadeStyles[status]),
                // Om vi får in en nodeRef får vi anta att den redan är inskickad till children
                ...hasExternalNodeRef ? {} : {
                    ref: actualRef
                }
            })
    });
};
// TODO: Trixande för korrekta typer med forwardRef och generisk komponent. Gör om när vi kör react 19.
export const Fade = /*#__PURE__*/ forwardRef(FadeFn);
Fade.displayName = 'Fade';
